import { Alert, Button, Drawer, Form, Input, InputNumber, Select } from 'antd';
import { DriverSearchSelect } from '../../drivers/public/DriverSearchSelect';
import { TruckSearchSelect } from '../../fleet/features/trucks/public/TruckSearchSelect';
import type { ExpenseCategoryView } from '../expenses.api';
import type { ExpenseFormValues } from '../policies/expense-form';

interface RecordExpenseDrawerProps {
  open: boolean;
  categories: ExpenseCategoryView[];
  error: string | null;
  submitting: boolean;
  onRecord: (values: ExpenseFormValues) => Promise<boolean>;
  onClose: () => void;
}

/**
 * A short form: whose expense, which truck, what it was for and how much. It records a cost with no trip (a truck's); a trip's
 * expense comes from the driver's app. Fuel asks for litres, station and odometer. Receipt photos are attached from the app.
 */
export function RecordExpenseDrawer({ open, categories, error, submitting, onRecord, onClose }: RecordExpenseDrawerProps) {
  const [form] = Form.useForm<ExpenseFormValues>();
  const categoryCode = Form.useWatch('categoryCode', form);
  const capturesFuel = categories.find((category) => category.code === categoryCode)?.capturesFuelDetails ?? false;

  async function submit(values: ExpenseFormValues) {
    if (await onRecord(values)) {
      form.resetFields();
      onClose();
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title="Record expense" placement="right" size={420} destroyOnHidden>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<ExpenseFormValues> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
        <Form.Item name="driverId" label="Driver" rules={[{ required: true, message: 'Choose the driver whose expense this is' }]}>
          <DriverSearchSelect />
        </Form.Item>
        <Form.Item name="truckId" label="Truck" rules={[{ required: true, message: 'Choose the truck it was spent on' }]}>
          <TruckSearchSelect />
        </Form.Item>
        <Form.Item name="categoryCode" label="Category" rules={[{ required: true, message: 'Choose a category' }]}>
          <Select placeholder="Select a category" options={categories.map((category) => ({ value: category.code, label: category.name }))} />
        </Form.Item>
        <Form.Item
          name="amount"
          label="Amount (KES)"
          rules={[
            { required: true, message: 'Enter the amount' },
            { type: 'number', min: 0.01, message: 'Must be more than zero' },
          ]}
        >
          <InputNumber className="w-full" min={0} precision={2} controls={false} />
        </Form.Item>
        {capturesFuel && (
          <>
            <Form.Item name="litres" label="Litres" rules={[{ required: true, message: 'Enter the litres' }]}>
              <InputNumber className="w-full" min={0.01} precision={2} controls={false} />
            </Form.Item>
            <Form.Item name="station" label="Station" rules={[{ required: true, message: 'Enter the station' }]}>
              <Input />
            </Form.Item>
            <Form.Item name="odometerKm" label="Odometer (km)" rules={[{ required: true, message: 'Enter the odometer reading' }]}>
              <InputNumber className="w-full" min={0} precision={0} controls={false} />
            </Form.Item>
          </>
        )}
        <Form.Item name="note" label="Note">
          <Input.TextArea rows={3} maxLength={500} />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            Record expense
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
